// ============================================================================
// Sensors Module
// ============================================================================
// Main view for sensors management with filtering and infinite scroll

import { auth } from '../core/auth.js';
import { t } from '../core/i18n.js';
import { showError, showSuccess } from '../core/ui.js';
import { updatePageTitle, updateActiveNavigation } from './navigation.js';
import { getEffectiveAccountId, getCurrentUser } from '../utils/helpers.js';
import { 
    showEditSensorModal, handleEditSensor, confirmDeleteSensor, deleteSensor,
    closeEditSensorModal, handleLocationAssetChange, showAssignAlarmModal,
    showTemplatePreview, handleAssignAlarm, toggleAlarmAssignment,
    removeAlarmAssignment, showSuspendAlarmModal, handleSuspendAlarm,
    resumeAlarmAssignment, closeNestedNestedModal
} from './sensor-edit.js';
import { loadAccountsForSensorModal } from './sensors-modals.js';

// Currently selected sensor ids for bulk actions (persists across table re-renders)
const selectedSensorIds = new Set();

// Sensors infinite scroll state
export const sensorsInfiniteScroll = {
    currentPage: 1,
    pageSize: 50,
    allSensors: [],
    totalCount: 0,
    hasMore: true,
    isLoading: false,
    searchQuery: '',
    filterAccountIds: [],
    filterBatteryStatus: ''
};

// Table sort state
const tableSortState = {
    sensors: { column: null, direction: 'asc' }
};

// Debounce timer for sensor search
let sensorSearchDebounceTimer = null;

// Scroll handler reference
let sensorsScrollHandler = null;

// ============================================================================
// Main View
// ============================================================================

export async function showSensors() {
    return showSetupSensors();
}

export async function showSetupSensors() {
    const mainContent = document.getElementById('main-content');
    if (!mainContent) return;

    if (!auth.isAuthenticated()) {
        showError('Please login to view sensors');
        return;
    }

    mainContent.innerHTML = `
        <!-- Enhanced Filter Row - Sticky -->
        <div class="sticky top-16 z-30 mb-0 bg-gray-50 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3">
            <div class="bg-gray-200 shadow rounded-lg p-4">
                <!-- Row 1: Add Sensor Button (prominent) -->
                <div class="flex justify-end gap-2 mb-2">
                    ${getCurrentUser()?.role === 'SuperAdmin' ? `
                    <button onclick="window.showImportSensorsModal()" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gray-700 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        ${t('sensors.importButton')}
                    </button>
                    ` : ''}
                    <button onclick="window.showCreateSensorModal()" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                        </svg>
                        ${t('sensors.addButton')}
                    </button>
                </div>
                
                <!-- Row 2: Filters and Action Buttons -->
                <div class="flex items-end justify-between gap-4">
                    <!-- Left side: Search, Account Filter (wider), Battery Filter -->
                    <div class="flex-1 grid grid-cols-1 md:grid-cols-12 gap-4">
                        <!-- Search Field -->
                        <div class="md:col-span-3">
                            <label for="sensor-search" class="block text-sm font-medium text-gray-700 mb-1">${t('sensors.search')}</label>
                            <input type="text" id="sensor-search" placeholder="${t('sensors.searchPlaceholder')}" 
                                   class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                   oninput="window.filterSensors()">
                        </div>
                        
                        <!-- Account Filter (wider - 6 columns) -->
                        <div class="md:col-span-6">
                            <label class="block text-sm font-medium text-gray-700 mb-1">${t('sensors.filterByAccount')}</label>
                            <div class="relative">
                                <!-- Collapsed view showing "Any account" -->
                                <button type="button" id="sensors-account-filter-toggle" 
                                        onclick="window.toggleSensorsAccountFilterExpanded()"
                                        class="w-full text-left border border-gray-300 rounded-md px-3 py-2 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                                    <div class="flex items-center justify-between">
                                        <span id="sensors-account-filter-display" class="text-sm text-gray-700">${t('sensors.anyAccount')}</span>
                                        <svg id="sensors-account-filter-arrow" class="w-4 h-4 text-gray-400 transform transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                                        </svg>
                                    </div>
                                </button>
                                <!-- Expanded tree view (hidden by default) -->
                                <div id="sensors-account-filter-tree-container" class="hidden absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                                    <div id="sensors-account-filter-tree" class="p-2 max-h-64 overflow-y-auto">
                                        <div class="text-gray-400 text-sm">${t('sensors.loadingAccounts')}</div>
                                    </div>
                                    <div class="border-t border-gray-200 px-3 py-2 bg-gray-50 flex items-center justify-end">
                                        <button type="button" onclick="window.clearSensorsAccountFilter()" class="text-sm text-gray-600 hover:text-gray-700">
                                            ${t('sensors.clearAll')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Battery Status Filter -->
                        <div class="md:col-span-3">
                            <label for="sensors-battery-filter" class="block text-sm font-medium text-gray-700 mb-1">${t('sensors.filterByBattery')}</label>
                            <select id="sensors-battery-filter" 
                                    onchange="window.applySensorsBatteryFilter()"
                                    class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                                <option value="">${t('sensors.allStatuses')}</option>
                                <option value="Red">${t('sensors.batteryRed')}</option>
                                <option value="Yellow">${t('sensors.batteryYellow')}</option>
                                <option value="Green">${t('sensors.batteryGreen')}</option>
                                <option value="No data">${t('sensors.batteryNoData')}</option>
                            </select>
                        </div>
                    </div>
                    
                    <!-- Right side: Action buttons -->
                    <div class="flex items-end gap-2">
                        <button onclick="window.clearSensorsFilters()" class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                            ${t('sensors.clear')}
                        </button>
                        <button onclick="window.loadSensorsData(true)" class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            ${t('sensors.refresh')}
                        </button>
                        <button onclick="window.showExportSensorsModal()" class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            ${t('sensors.export')}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Sensors Content -->
        <div class="px-0">
            <!-- Sensor Count Display -->
            <div id="sensors-count-info" class="mb-4 px-1">
                <div class="flex items-center justify-between">
                    <div class="text-sm text-gray-600">
                        <span id="sensors-total-count">${t('common.loading')}</span>
                    </div>
                    <div class="text-xs text-gray-400 italic">
                        ${t('sensors.customizeColumns')}
                    </div>
                </div>
            </div>
            
            <div id="sensors-content">
                <div class="text-center py-12">
                    <div class="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm shadow rounded-md text-gray-500 bg-white">
                        <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        ${t('sensors.loading')}
                    </div>
                </div>
            </div>
        </div>
    `;

    // Set page title
    updatePageTitle(t('sensors.title'));

    // Update navigation
    updateActiveNavigation('setup-sensors');

    // Load column preferences
    loadSensorsColumnPreferences();

    // Reset infinite scroll state
    sensorsInfiniteScroll.currentPage = 1;
    sensorsInfiniteScroll.allSensors = [];
    sensorsInfiniteScroll.totalCount = 0;
    sensorsInfiniteScroll.hasMore = true;
    sensorsInfiniteScroll.isLoading = false;

    // Reset bulk selection when (re)entering the page
    selectedSensorIds.clear();
    const lingeringBar = document.getElementById('sensors-mass-action-bar');
    if (lingeringBar) lingeringBar.remove();

    // Load initial data
    await loadSensorsAccountFilter();
    await loadSensorsData(true);
    setupSensorsInfiniteScroll();
}

// ============================================================================
// Data Loading
// ============================================================================

export async function loadSensorsData(isInitialLoad = false) {
    if (sensorsInfiniteScroll.isLoading || (!isInitialLoad && !sensorsInfiniteScroll.hasMore)) {
        return;
    }

    // Reset pagination for initial load
    if (isInitialLoad) {
        sensorsInfiniteScroll.currentPage = 1;
        sensorsInfiniteScroll.hasMore = true;
    }

    sensorsInfiniteScroll.isLoading = true;

    try {
        const params = new URLSearchParams({
            page: sensorsInfiniteScroll.currentPage,
            limit: sensorsInfiniteScroll.pageSize
        });

        const skip = (sensorsInfiniteScroll.currentPage - 1) * sensorsInfiniteScroll.pageSize;
        params.append('skip', skip);

        if (sensorsInfiniteScroll.searchQuery) {
            params.append('search', sensorsInfiniteScroll.searchQuery);
        }

        if (sensorsInfiniteScroll.filterAccountIds && sensorsInfiniteScroll.filterAccountIds.length > 0) {
            params.append('account_ids', sensorsInfiniteScroll.filterAccountIds.join(','));
        }

        if (sensorsInfiniteScroll.filterBatteryStatus && sensorsInfiniteScroll.filterBatteryStatus !== '') {
            params.append('battery_status', sensorsInfiniteScroll.filterBatteryStatus);
        }

        // Add sort parameters
        const currentSort = tableSortState.sensors;
        if (currentSort && currentSort.column) {
            params.append('sort_by', currentSort.column);
            params.append('sort_order', currentSort.direction);
        }

        const response = await fetch(`/api/sensors?${params}`, {
            headers: { 'Authorization': `Bearer ${auth.token}` }
        });

        if (response.ok) {
            const data = await response.json();
            const sensors = data.sensors || [];
            
            // Store total count for display
            sensorsInfiniteScroll.totalCount = data.total || 0;
            
            if (isInitialLoad) {
                sensorsInfiniteScroll.allSensors = sensors;
            } else {
                sensorsInfiniteScroll.allSensors = [...sensorsInfiniteScroll.allSensors, ...sensors];
            }
            
            sensorsInfiniteScroll.hasMore = sensors.length === sensorsInfiniteScroll.pageSize;
            sensorsInfiniteScroll.currentPage += 1;
            
            displaySensors(sensorsInfiniteScroll.allSensors);
            updateSensorsCountDisplay();
        } else {
            throw new Error('Failed to fetch sensors');
        }
    } catch (error) {
        console.error('Error loading sensors:', error);
        showError('Failed to load sensors');
    } finally {
        sensorsInfiniteScroll.isLoading = false;
    }
}

async function loadSensorsAccountFilter() {
    try {
        const accountId = getEffectiveAccountId();
        if (!accountId) {
            console.log('No account selected, skipping account filter load');
            return;
        }
        
        const response = await fetch(`/api/accounts/hierarchy?account_id=${accountId}`, {
            headers: { 'Authorization': `Bearer ${auth.token}` }
        });

        if (response.ok) {
            const data = await response.json();
            const accounts = data.accounts_tree || [];
            renderSensorsAccountFilterTree(accounts);
        }
    } catch (error) {
        console.error('Error loading accounts for filter:', error);
    }
}

// ============================================================================
// Display Functions
// ============================================================================

function displaySensors(sensors) {
    const container = document.getElementById('sensors-content');
    if (!container) return;

    if (sensors.length === 0) {
        container.innerHTML = `
            <div class="text-center py-12">
                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.348 14.652a3 3 0 0 1 0-4.304m1.304 0a1.5 1.5 0 0 1 0 2.152M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                </svg>
                <h3 class="mt-2 text-sm font-medium text-gray-900">No sensors</h3>
                <p class="mt-1 text-sm text-gray-500">Get started by adding a new sensor.</p>
                <div class="mt-6">
                    <button onclick="window.showCreateSensorModal()" class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                        </svg>
                        Add Sensor
                    </button>
                </div>
            </div>
        `;
        updateSensorsCountDisplay();
        return;
    }

    // Generate table headers
    const columnsInOrder = getSensorsColumnsInOrder().filter(col => col.visible);
    const headers = columnsInOrder.map(col => {
        return `
            <th scope="col" class="sortable-header px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-200" onclick="window.sortSensorsTable('${col.key}')">
                <div class="flex items-center space-x-1">
                    <span>${col.label}</span>
                    <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                    </svg>
                </div>
            </th>
        `;
    }).join('');

    container.innerHTML = `
        <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table id="sensors-table" class="min-w-full divide-y divide-gray-300">
                <thead class="bg-gray-200" oncontextmenu="window.showSensorColumnMenu(event); return false;">
                    <tr>
                        <th scope="col" class="px-4 py-3 bg-gray-200 w-10">
                            <input type="checkbox" id="sensors-select-all" onchange="window.toggleSelectAllSensors(this)"
                                   class="rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer">
                        </th>
                        ${headers}
                    </tr>
                </thead>
                <tbody id="sensors-table-body" class="bg-white divide-y divide-gray-200">
                    ${generateSensorRows(sensors)}
                </tbody>
            </table>
        </div>
    `;

    updateSensorsBulkBar();
}

function generateSensorRows(sensors) {
    // Helper to get alarm status icon
    const getAlarmStatusIcon = (sensor) => {
        const alarmInfo = sensor.alarm_status || {};
        const hasAlarms = alarmInfo.total_alarms > 0;
        
        if (!hasAlarms) return '';
        
        let colorClass, title;
        
        if (alarmInfo.suspended_count > 0) {
            colorClass = 'text-yellow-500';
            title = `${alarmInfo.suspended_count} alarm(s) suspended`;
        } else if (alarmInfo.active_count > 0) {
            colorClass = 'text-green-500';
            title = `${alarmInfo.active_count} active alarm(s)`;
        } else {
            colorClass = 'text-gray-400';
            title = `${alarmInfo.total_alarms} alarm(s) disabled`;
        }
        
        return `
            <svg class="w-5 h-5 ${colorClass} mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" title="${title}">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
        `;
    };

    const columnRenderers = {
        name: (sensor) => `
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div class="flex items-center">
                    ${getAlarmStatusIcon(sensor)}
                    <button onclick="window.showEditSensorModal('${sensor.id || sensor._id}')" class="font-medium text-primary-600 hover:text-primary-900 cursor-pointer">${sensor.name || 'Unnamed Sensor'}</button>
                </div>
            </td>
        `,
        sensor_id: (sensor) => `
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <code class="bg-gray-100 px-2 py-1 rounded text-xs">${sensor.sensor_id || 'N/A'}</code>
            </td>
        `,
        sensor_type: (sensor) => {
            const sensorTypeLabels = {
                'card_reader': 'Card Reader',
                'temperature': 'Temperature',
                'temperature_humidity': 'Temperature/Humidity/Pressure',
                'temp_humidity': 'Temperature/Humidity',
                'tracker': 'Tracker',
                'gateway': 'Gateway',
                'activity': 'Activity',
                'light': 'Light'
            };
            const typeLabel = sensorTypeLabels[sensor.sensor_type] || (sensor.sensor_type ? sensor.sensor_type.replace('_', '/').replace(/\b\w/g, l => l.toUpperCase()) : 'Not set');
            return `
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div class="flex items-center">
                    <svg class="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                    </svg>
                    ${typeLabel}
                </div>
            </td>
        `;
        },
        account_name: (sensor) => `
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div class="flex items-center">
                    <svg class="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    ${sensor.account_name || 'Not assigned'}
                </div>
            </td>
        `,
        location_name: (sensor) => `
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div class="flex items-center">
                    <svg class="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    ${sensor.location_name || 'Not assigned'}
                </div>
            </td>
        `,
        asset_name: (sensor) => `
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div class="flex items-center">
                    <svg class="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-16.5 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21" />
                    </svg>
                    ${sensor.asset_name || 'Not assigned'}
                </div>
            </td>
        `,
        battery_status: (sensor) => {
            const displayLevel = sensor.battery_level !== null && sensor.battery_level !== undefined ? sensor.battery_level : 'N/A';
            const badges = {
                'Green': `<span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">🟢 ${displayLevel}</span>`,
                'Yellow': `<span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">🟡 ${displayLevel}</span>`,
                'Red': `<span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">🔴 ${displayLevel}</span>`,
                'No data': '<span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">⚪ No data</span>'
            };
            const badge = badges[sensor.battery_status] || `<span class="text-gray-500">Unknown (${displayLevel})</span>`;
            return `<td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${badge}</td>`;
        },
        battery_level: (sensor) => `
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                ${sensor.battery_level || '-'}
            </td>
        `,
        last_data_received: (sensor) => {
            if (!sensor.last_data_received) {
                return `<td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">No data yet</td>`;
            }
            const date = new Date(sensor.last_data_received);
            const formattedDate = date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
            const formattedTime = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
            return `
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                    <button onclick="window.showSensorLogsModal('${sensor.id || sensor._id}', '${sensor.sensor_id}')" class="text-primary-600 hover:text-primary-900 cursor-pointer hover:underline">
                        <div class="flex flex-col">
                            <span class="font-medium">${formattedDate}</span>
                            <span class="text-xs text-gray-500">${formattedTime}</span>
                        </div>
                    </button>
                </td>
            `;
        }
    };
    
    return sensors.map(sensor => {
        const columnsInOrder = getSensorsColumnsInOrder().filter(col => col.visible);
        const cells = columnsInOrder.map(col => columnRenderers[col.key](sensor)).join('');
        const sid = sensor.id || sensor._id;
        const checked = selectedSensorIds.has(sid) ? 'checked' : '';
        const selectCell = `
            <td class="px-4 py-4 whitespace-nowrap w-10">
                <input type="checkbox" class="sensor-select-checkbox rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                       value="${sid}" ${checked} onchange="window.onSensorSelectChange(this)">
            </td>`;
        return `<tr class="hover:bg-gray-50">${selectCell}${cells}</tr>`;
    }).join('');
}

function updateSensorsCountDisplay() {
    const countElement = document.getElementById('sensors-total-count');
    if (!countElement) return;
    
    const displayedCount = sensorsInfiniteScroll.allSensors.length;
    const totalCount = sensorsInfiniteScroll.totalCount;
    
    // Check for active filters
    const hasSearch = sensorsInfiniteScroll.searchQuery && sensorsInfiniteScroll.searchQuery.trim() !== '';
    const hasAccountFilter = sensorsInfiniteScroll.filterAccountIds && sensorsInfiniteScroll.filterAccountIds.length > 0;
    const hasBatteryFilter = sensorsInfiniteScroll.filterBatteryStatus && sensorsInfiniteScroll.filterBatteryStatus !== '';
    const hasFilters = hasSearch || hasAccountFilter || hasBatteryFilter;
    
    if (hasFilters) {
        let filterDescription = [];
        if (hasSearch) filterDescription.push(`${t('sensors.search')}: "${sensorsInfiniteScroll.searchQuery}"`);
        if (hasAccountFilter) filterDescription.push(t('sensors.accountFilter'));
        if (hasBatteryFilter) filterDescription.push(`${t('sensors.battery')}: ${sensorsInfiniteScroll.filterBatteryStatus}`);
        
        countElement.innerHTML = `
            <div class="flex items-center space-x-2">
                <span class="font-medium">${t('sensors.showing')} ${displayedCount.toLocaleString()} ${t('sensors.of')} ${totalCount.toLocaleString()} ${t('sensors.sensorsLowercase')}</span>
                <span class="text-gray-400">•</span>
                <span class="text-blue-600">${filterDescription.join(', ')}</span>
            </div>
        `;
    } else {
        countElement.innerHTML = `
            <span class="font-medium">${totalCount.toLocaleString()} ${totalCount === 1 ? t('sensors.sensor') : t('sensors.sensorsLowercase')}</span>
        `;
    }
}

function renderSensorsAccountFilterTree(accounts, parentElement = null, level = 0) {
    const container = parentElement || document.getElementById('sensors-account-filter-tree');
    if (!container) return;

    if (!parentElement) {
        container.innerHTML = '';
    }

    accounts.forEach(account => {
        const itemDiv = document.createElement('div');
        itemDiv.className = 'mb-1';
        itemDiv.style.paddingLeft = `${level * 16}px`;

        const hasChildren = account.children && account.children.length > 0;
        const accountId = account._id || account.id;
        const accountName = account.account_name || account.name || 'Unnamed Account';
        
        itemDiv.innerHTML = `
            <div class="flex items-center py-1 hover:bg-gray-50 rounded">
                ${hasChildren ? `
                    <button onclick="window.toggleSensorsAccountTreeItem('tree-item-${accountId}')" class="mr-1 text-gray-400 hover:text-gray-600 focus:outline-none">
                        <svg id="tree-item-${accountId}-icon" class="w-4 h-4 transform transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                ` : '<span class="w-5"></span>'}
                <label class="flex items-center cursor-pointer flex-1">
                    <input type="checkbox" value="${accountId}" onchange="window.applySensorsAccountFilter()" class="rounded border-gray-300 text-primary-600 focus:ring-primary-500">
                    <span class="ml-2 text-sm text-gray-700">${accountName}</span>
                </label>
            </div>
            ${hasChildren ? `<div id="tree-item-${accountId}" class="hidden"></div>` : ''}
        `;

        container.appendChild(itemDiv);

        if (hasChildren) {
            const childContainer = itemDiv.querySelector(`#tree-item-${accountId}`);
            renderSensorsAccountFilterTree(account.children, childContainer, level + 1);
        }
    });
}

// ============================================================================
// Filter Functions
// ============================================================================

export function filterSensors() {
    if (sensorSearchDebounceTimer) {
        clearTimeout(sensorSearchDebounceTimer);
    }
    
    sensorSearchDebounceTimer = setTimeout(() => {
        const searchInput = document.getElementById('sensor-search');
        if (searchInput) {
            sensorsInfiniteScroll.searchQuery = searchInput.value.trim();
            sensorsInfiniteScroll.currentPage = 1;
            sensorsInfiniteScroll.allSensors = [];
            sensorsInfiniteScroll.hasMore = true;
            loadSensorsData(true);
        }
    }, 300);
}

export function toggleSensorsAccountFilterExpanded() {
    const container = document.getElementById('sensors-account-filter-tree-container');
    const arrow = document.getElementById('sensors-account-filter-arrow');
    
    if (container && arrow) {
        const isHidden = container.classList.contains('hidden');
        if (isHidden) {
            container.classList.remove('hidden');
            arrow.style.transform = 'rotate(180deg)';
            // Add click-outside listener
            setTimeout(() => {
                document.addEventListener('click', closeSensorsFilterOnClickOutside);
            }, 0);
        } else {
            container.classList.add('hidden');
            arrow.style.transform = 'rotate(0deg)';
            document.removeEventListener('click', closeSensorsFilterOnClickOutside);
        }
    }
}

function closeSensorsFilterOnClickOutside(event) {
    const container = document.getElementById('sensors-account-filter-tree-container');
    const button = event.target.closest('[onclick*="toggleSensorsAccountFilterExpanded"]');
    
    if (container && !container.contains(event.target) && !button) {
        container.classList.add('hidden');
        const arrow = document.getElementById('sensors-account-filter-arrow');
        if (arrow) arrow.style.transform = 'rotate(0deg)';
        document.removeEventListener('click', closeSensorsFilterOnClickOutside);
    }
}

export function toggleSensorsAccountTreeItem(itemId) {
    const element = document.getElementById(itemId);
    const icon = document.getElementById(`${itemId}-icon`);
    
    if (element && icon) {
        const isHidden = element.classList.contains('hidden');
        if (isHidden) {
            element.classList.remove('hidden');
            icon.style.transform = 'rotate(90deg)';
        } else {
            element.classList.add('hidden');
            icon.style.transform = 'rotate(0deg)';
        }
    }
}

export function applySensorsAccountFilter() {
    const checkboxes = document.querySelectorAll('#sensors-account-filter-tree input[type="checkbox"]:checked');
    sensorsInfiniteScroll.filterAccountIds = Array.from(checkboxes).map(cb => cb.value);
    
    updateSensorsAccountFilterDisplay();
    
    sensorsInfiniteScroll.currentPage = 1;
    sensorsInfiniteScroll.allSensors = [];
    sensorsInfiniteScroll.hasMore = true;
    
    loadSensorsData(true);
}

export function clearSensorsAccountFilter() {
    const checkboxes = document.querySelectorAll('#sensors-account-filter-tree input[type="checkbox"]');
    checkboxes.forEach(cb => cb.checked = false);
    
    sensorsInfiniteScroll.filterAccountIds = [];
    updateSensorsAccountFilterDisplay();
    
    sensorsInfiniteScroll.currentPage = 1;
    sensorsInfiniteScroll.allSensors = [];
    sensorsInfiniteScroll.hasMore = true;
    
    loadSensorsData(true);
}

function updateSensorsAccountFilterDisplay() {
    const display = document.getElementById('sensors-account-filter-display');
    if (!display) return;

    if (sensorsInfiniteScroll.filterAccountIds.length === 0) {
        display.textContent = t('sensors.anyAccount');
    } else if (sensorsInfiniteScroll.filterAccountIds.length === 1) {
        const checkbox = document.querySelector(`#sensors-account-filter-tree input[value="${sensorsInfiniteScroll.filterAccountIds[0]}"]`);
        if (checkbox && checkbox.nextElementSibling) {
            display.textContent = checkbox.nextElementSibling.textContent.trim();
        } else {
            display.textContent = `1 ${t('sensors.accountSelected')}`;
        }
    } else {
        display.textContent = `${sensorsInfiniteScroll.filterAccountIds.length} ${t('sensors.accountsSelected')}`;
    }
}

export function applySensorsBatteryFilter() {
    const batteryFilter = document.getElementById('sensors-battery-filter');
    if (batteryFilter) {
        sensorsInfiniteScroll.filterBatteryStatus = batteryFilter.value;
        sensorsInfiniteScroll.currentPage = 1;
        sensorsInfiniteScroll.allSensors = [];
        sensorsInfiniteScroll.hasMore = true;
        loadSensorsData(true);
    }
}

export function clearSensorsFilters() {
    const searchInput = document.getElementById('sensor-search');
    if (searchInput) {
        searchInput.value = '';
        sensorsInfiniteScroll.searchQuery = '';
    }
    
    const checkboxes = document.querySelectorAll('#sensors-account-filter-tree input[type="checkbox"]');
    checkboxes.forEach(cb => cb.checked = false);
    sensorsInfiniteScroll.filterAccountIds = [];
    updateSensorsAccountFilterDisplay();
    
    const batteryFilter = document.getElementById('sensors-battery-filter');
    if (batteryFilter) {
        batteryFilter.value = '';
        sensorsInfiniteScroll.filterBatteryStatus = '';
    }
    
    sensorsInfiniteScroll.currentPage = 1;
    sensorsInfiniteScroll.allSensors = [];
    sensorsInfiniteScroll.hasMore = true;
    
    loadSensorsData(true);
}

// ============================================================================
// Infinite Scroll
// ============================================================================

function setupSensorsInfiniteScroll() {
    if (sensorsScrollHandler) {
        window.removeEventListener('scroll', sensorsScrollHandler);
    }
    
    sensorsScrollHandler = function() {
        const scrollPosition = window.scrollY + window.innerHeight;
        const scrollHeight = document.documentElement.scrollHeight;

        if (scrollPosition >= scrollHeight - 300 && !sensorsInfiniteScroll.isLoading && sensorsInfiniteScroll.hasMore) {
            loadSensorsData(false);
        }
    };
    
    window.addEventListener('scroll', sensorsScrollHandler);
}

// ============================================================================
// Column Preferences
// ============================================================================

// Column visibility configuration for sensors
const sensorsColumnConfig = {
    name: { labelKey: 'common.name', visible: true, sortable: true, default: true, order: 0 },
    sensor_id: { labelKey: 'sensors.sensorId', visible: true, sortable: true, default: true, order: 1 },
    sensor_type: { labelKey: 'sensors.type', visible: false, sortable: true, default: false, order: 2 },
    account_name: { labelKey: 'sensors.account', visible: true, sortable: true, default: true, order: 3 },
    location_name: { labelKey: 'sensors.location', visible: true, sortable: true, default: true, order: 4 },
    asset_name: { labelKey: 'sensors.asset', visible: false, sortable: true, default: false, order: 5 },
    battery_status: { labelKey: 'sensors.battery', visible: true, sortable: true, default: true, order: 6 },
    battery_level: { labelKey: 'sensors.batteryLevel', visible: false, sortable: true, default: false, order: 7 },
    last_data_received: { labelKey: 'sensors.lastData', visible: true, sortable: true, default: true, order: 8 }
};

function getSensorsColumnsInOrder() {
    return Object.entries(sensorsColumnConfig)
        .sort((a, b) => a[1].order - b[1].order)
        .map(([key, config]) => ({ key, ...config, label: t(config.labelKey) }));
}

function loadSensorsColumnPreferences() {
    const saved = localStorage.getItem('sensorsColumnConfig');
    if (saved) {
        try {
            const preferences = JSON.parse(saved);
            Object.keys(preferences).forEach(key => {
                if (sensorsColumnConfig[key]) {
                    if (preferences[key].visible !== undefined) {
                        sensorsColumnConfig[key].visible = preferences[key].visible;
                    }
                    if (preferences[key].order !== undefined) {
                        sensorsColumnConfig[key].order = preferences[key].order;
                    }
                }
            });
        } catch (e) {
            console.error('Error loading column preferences:', e);
        }
    }
}

function saveSensorsColumnPreferences() {
    const preferences = {};
    Object.keys(sensorsColumnConfig).forEach(key => {
        preferences[key] = {
            visible: sensorsColumnConfig[key].visible,
            order: sensorsColumnConfig[key].order
        };
    });
    localStorage.setItem('sensorsColumnConfig', JSON.stringify(preferences));
}

function toggleSensorColumn(columnKey) {
    if (sensorsColumnConfig[columnKey]) {
        sensorsColumnConfig[columnKey].visible = !sensorsColumnConfig[columnKey].visible;
        saveSensorsColumnPreferences();
        displaySensors(sensorsInfiniteScroll.allSensors);
        
        // Update the checkmark in the context menu if it's open
        const menu = document.getElementById('column-context-menu');
        if (menu) {
            const menuItem = menu.querySelector(`.column-menu-item[data-column-key="${columnKey}"]`);
            if (menuItem) {
                // Find the checkmark SVG (second SVG in the item, first is drag handle)
                const svgs = menuItem.querySelectorAll('svg');
                if (svgs.length >= 2) {
                    const checkSvg = svgs[1]; // Second SVG is the checkmark
                    if (sensorsColumnConfig[columnKey].visible) {
                        checkSvg.classList.remove('text-transparent');
                        checkSvg.classList.add('text-primary-600');
                    } else {
                        checkSvg.classList.remove('text-primary-600');
                        checkSvg.classList.add('text-transparent');
                    }
                }
            }
        }
    }
}

function resetSensorColumnsToDefault() {
    const defaultOrder = {
        name: 0, sensor_id: 1, sensor_type: 2, account_name: 3,
        location_name: 4, asset_name: 5, battery_status: 6, battery_level: 7, last_data_received: 8
    };
    
    Object.keys(sensorsColumnConfig).forEach(key => {
        sensorsColumnConfig[key].visible = sensorsColumnConfig[key].default;
        sensorsColumnConfig[key].order = defaultOrder[key];
    });
    saveSensorsColumnPreferences();
    displaySensors(sensorsInfiniteScroll.allSensors);
    
    // Close and reopen the menu to reflect changes
    const menu = document.getElementById('column-context-menu');
    if (menu) {
        menu.remove();
    }
}

function showSensorColumnMenu(event) {
    event.preventDefault();
    
    // Remove any existing menu
    const existingMenu = document.getElementById('column-context-menu');
    if (existingMenu) {
        existingMenu.remove();
    }
    
    // Create context menu
    const menu = document.createElement('div');
    menu.id = 'column-context-menu';
    menu.className = 'fixed bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50';
    menu.style.left = event.pageX + 'px';
    menu.style.top = event.pageY + 'px';
    menu.style.minWidth = '250px';
    
    // Get columns in current order
    const columnsInOrder = getSensorsColumnsInOrder();
    
    const menuItems = columnsInOrder.map((column) => {
        const checkIcon = column.visible ? 
            '<svg class="w-4 h-4 text-primary-600" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path></svg>' :
            '<svg class="w-4 h-4 text-transparent" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path></svg>';
        
        return `
            <div 
                draggable="true"
                data-column-key="${column.key}"
                class="column-menu-item flex items-center px-4 py-2 hover:bg-gray-100 cursor-move select-none"
                onclick="event.stopPropagation(); window.toggleSensorColumn('${column.key}')"
            >
                <svg class="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16" />
                </svg>
                ${checkIcon}
                <span class="text-sm text-gray-700 ml-2">${column.label}</span>
            </div>
        `;
    }).join('');
    
    menu.innerHTML = `
        <div class="px-4 py-2 border-b border-gray-200">
            <p class="text-xs font-semibold text-gray-500 uppercase">Column Order & Visibility</p>
            <p class="text-xs text-gray-400 mt-1">Drag to reorder, click to toggle</p>
        </div>
        <div id="column-menu-items">
            ${menuItems}
        </div>
        <div class="border-t border-gray-200 mt-2">
            <button 
                onclick="window.resetSensorColumnsToDefault()"
                class="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm text-primary-600"
            >
                Reset to Default
            </button>
        </div>
    `;
    
    document.body.appendChild(menu);
    
    // Setup drag and drop for column reordering
    setupColumnDragAndDrop();
    
    // Close menu when clicking outside
    const closeMenu = (e) => {
        if (!menu.contains(e.target)) {
            menu.remove();
            document.removeEventListener('click', closeMenu);
        }
    };
    
    // Delay adding the click listener to prevent immediate closing
    setTimeout(() => {
        document.addEventListener('click', closeMenu);
    }, 100);
}

function setupColumnDragAndDrop() {
    const items = document.querySelectorAll('.column-menu-item');
    let draggedItem = null;
    
    items.forEach(item => {
        item.addEventListener('dragstart', function(e) {
            draggedItem = this;
            this.style.opacity = '0.5';
            e.dataTransfer.effectAllowed = 'move';
        });
        
        item.addEventListener('dragend', function(e) {
            this.style.opacity = '1';
            
            // Update orders based on new positions
            const menuItems = document.querySelectorAll('.column-menu-item');
            menuItems.forEach((item, index) => {
                const key = item.getAttribute('data-column-key');
                sensorsColumnConfig[key].order = index;
            });
            
            saveSensorsColumnPreferences();
            displaySensors(sensorsInfiniteScroll.allSensors);
        });
        
        item.addEventListener('dragover', function(e) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            
            if (this === draggedItem) return;
            
            const container = this.parentNode;
            const allItems = [...container.querySelectorAll('.column-menu-item')];
            const draggedIndex = allItems.indexOf(draggedItem);
            const targetIndex = allItems.indexOf(this);
            
            if (draggedIndex < targetIndex) {
                this.parentNode.insertBefore(draggedItem, this.nextSibling);
            } else {
                this.parentNode.insertBefore(draggedItem, this);
            }
        });
    });
}

// ============================================================================
// Bulk Account Assignment
// ============================================================================

export function onSensorSelectChange(checkbox) {
    const id = checkbox.value;
    if (checkbox.checked) {
        selectedSensorIds.add(id);
    } else {
        selectedSensorIds.delete(id);
    }
    syncSelectAllCheckbox();
    updateSensorsBulkBar();
}

export function toggleSelectAllSensors(headerCheckbox) {
    const checkboxes = document.querySelectorAll('.sensor-select-checkbox');
    checkboxes.forEach(cb => {
        cb.checked = headerCheckbox.checked;
        if (headerCheckbox.checked) {
            selectedSensorIds.add(cb.value);
        } else {
            selectedSensorIds.delete(cb.value);
        }
    });
    updateSensorsBulkBar();
}

export function clearSensorSelection() {
    selectedSensorIds.clear();
    document.querySelectorAll('.sensor-select-checkbox').forEach(cb => { cb.checked = false; });
    const selectAll = document.getElementById('sensors-select-all');
    if (selectAll) selectAll.checked = false;
    updateSensorsBulkBar();
}

function syncSelectAllCheckbox() {
    const selectAll = document.getElementById('sensors-select-all');
    if (!selectAll) return;
    const checkboxes = Array.from(document.querySelectorAll('.sensor-select-checkbox'));
    selectAll.checked = checkboxes.length > 0 && checkboxes.every(cb => cb.checked);
}

function updateSensorsBulkBar() {
    const count = selectedSensorIds.size;
    let actionBar = document.getElementById('sensors-mass-action-bar');

    if (count > 0) {
        if (!actionBar) {
            actionBar = document.createElement('div');
            actionBar.id = 'sensors-mass-action-bar';
            actionBar.className = 'fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-200 rounded-lg shadow-2xl border border-gray-300 px-6 py-4 z-40 flex items-center space-x-6';
            actionBar.innerHTML = `
                <div class="flex items-center space-x-2 text-sm font-medium text-gray-700">
                    <span id="sensors-selected-count">${count}</span>
                    <span>${t('sensors.selected') || 'selected'}</span>
                </div>
                <div class="h-6 w-px bg-gray-300"></div>
                <button onclick="window.showBulkAssignAccountModal()"
                        class="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
                        title="${t('sensors.bulkAssignAccount') || 'Assign to account'}">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                    </svg>
                    <span>${t('sensors.bulkAssignAccount') || 'Assign to account'}</span>
                </button>
                <div class="h-6 w-px bg-gray-300"></div>
                <button onclick="window.clearSensorSelection()"
                        class="text-sm text-gray-600 hover:text-gray-900 font-medium"
                        title="${t('sensors.clearSelection') || 'Clear'}">
                    ${t('sensors.clearSelection') || 'Clear'}
                </button>
            `;
            document.body.appendChild(actionBar);
        } else {
            const countSpan = actionBar.querySelector('#sensors-selected-count');
            if (countSpan) countSpan.textContent = count;
        }
    } else if (actionBar) {
        actionBar.remove();
    }

    syncSelectAllCheckbox();
}

export async function showBulkAssignAccountModal() {
    if (selectedSensorIds.size === 0) return;

    const modalHtml = `
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
            <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div class="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                    <h3 class="text-lg font-medium text-gray-900">${t('sensors.bulkAssignAccount') || 'Assign to account'}</h3>
                </div>
                <div class="px-6 py-4 space-y-4">
                    <p class="text-sm text-gray-600">
                        ${(t('sensors.bulkAssignDescription') || 'Move {count} selected sensor(s) to the account below. Any location or asset association will be cleared.').replace('{count}', selectedSensorIds.size)}
                    </p>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                            ${t('locations.selectAccount') || 'Select account'} <span class="text-red-500">*</span>
                        </label>
                        <div id="bulk-sensor-account-tree-container" class="border border-gray-300 rounded-md p-3 max-h-64 overflow-y-auto bg-white">
                            <div class="text-gray-400 text-sm">${t('sensors.loadingAccounts') || 'Loading accounts…'}</div>
                        </div>
                        <input type="hidden" id="bulk-sensor-account">
                    </div>
                    <div id="bulk-sensor-account-error" class="hidden text-sm text-red-600"></div>
                    <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                        <button type="button" onclick="window.closeModal()"
                                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                            ${t('common.cancel') || 'Cancel'}
                        </button>
                        <button type="button" id="bulk-assign-confirm-btn" onclick="window.confirmBulkAssignAccount()"
                                class="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700">
                            ${t('sensors.bulkAssignConfirm') || 'Assign'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `;

    const modal = document.getElementById('modal');
    modal.innerHTML = modalHtml;
    modal.classList.remove('hidden');

    await loadAccountsForSensorModal('bulk-sensor-account-tree-container', 'bulk-sensor-account');
}

export async function confirmBulkAssignAccount() {
    const accountId = document.getElementById('bulk-sensor-account')?.value || '';
    const errEl = document.getElementById('bulk-sensor-account-error');
    const btn = document.getElementById('bulk-assign-confirm-btn');

    if (!accountId) {
        if (errEl) {
            errEl.textContent = t('sensors.selectAccountRequired') || 'Please select an account.';
            errEl.classList.remove('hidden');
        }
        return;
    }

    const sensorIds = Array.from(selectedSensorIds);
    if (btn) {
        btn.disabled = true;
        btn.textContent = t('common.processing') || 'Processing…';
    }

    try {
        const response = await fetch('/api/sensors/bulk-assign-account', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${auth.token}`
            },
            body: JSON.stringify({ sensor_ids: sensorIds, account_id: accountId })
        });

        if (!response.ok) {
            let detail = `HTTP ${response.status}`;
            try { const e = await response.json(); detail = e.detail || detail; } catch (_) { /* ignore */ }
            throw new Error(detail);
        }

        const result = await response.json();
        if (typeof window.closeModal === 'function') window.closeModal();

        const parts = [`${t('sensors.bulkUpdated') || 'Updated'}: ${result.updated}`];
        if (result.skipped > 0) parts.push(`${t('sensors.bulkSkipped') || 'Skipped'}: ${result.skipped}`);
        showSuccess(`${parts.join(' · ')}${result.account_name ? ` → ${result.account_name}` : ''}`);

        selectedSensorIds.clear();
        await loadSensorsData(true);
    } catch (e) {
        if (errEl) {
            errEl.textContent = e.message || (t('common.error') || 'Operation failed');
            errEl.classList.remove('hidden');
        }
        if (btn) {
            btn.disabled = false;
            btn.textContent = t('sensors.bulkAssignConfirm') || 'Assign';
        }
    }
}

// ============================================================================
// Table Sorting (stub)
// ============================================================================

export function sortSensorsTable(column) {
    const currentSort = tableSortState.sensors;
    
    if (currentSort.column === column) {
        currentSort.direction = currentSort.direction === 'asc' ? 'desc' : 'asc';
    } else {
        currentSort.column = column;
        currentSort.direction = 'asc';
    }
    
    sensorsInfiniteScroll.currentPage = 1;
    sensorsInfiniteScroll.allSensors = [];
    sensorsInfiniteScroll.hasMore = true;
    
    loadSensorsData(true);
}

// ============================================================================
// Sensor Edit Functions (imported from sensor-edit.js)
// ============================================================================

// Re-export sensor edit functions for use within this module
export { 
    showEditSensorModal, handleEditSensor, confirmDeleteSensor, deleteSensor,
    closeEditSensorModal, handleLocationAssetChange, showAssignAlarmModal,
    showTemplatePreview, handleAssignAlarm, toggleAlarmAssignment,
    removeAlarmAssignment, showSuspendAlarmModal, handleSuspendAlarm,
    resumeAlarmAssignment, closeNestedNestedModal
};

// ============================================================================
// Global Exports (for onclick handlers)
// ============================================================================

window.filterSensors = filterSensors;
// Export to window for onclick handlers
window.showEditSensorModal = showEditSensorModal;
window.handleEditSensor = handleEditSensor;
window.onEditSensorTypeChange = (value) => {
    const wrapper = document.getElementById('edit-sensor-mac-wrapper');
    if (wrapper) wrapper.classList.toggle('hidden', value !== 'temp_humidity');
};
window.confirmDeleteSensor = confirmDeleteSensor;
window.deleteSensor = deleteSensor;
window.closeEditSensorModal = closeEditSensorModal;
window.handleLocationAssetChange = handleLocationAssetChange;
window.showAssignAlarmModal = showAssignAlarmModal;
window.showTemplatePreview = showTemplatePreview;
window.handleAssignAlarm = handleAssignAlarm;
window.toggleAlarmAssignment = toggleAlarmAssignment;
window.removeAlarmAssignment = removeAlarmAssignment;
window.showSuspendAlarmModal = showSuspendAlarmModal;
window.handleSuspendAlarm = handleSuspendAlarm;
window.resumeAlarmAssignment = resumeAlarmAssignment;
window.closeNestedNestedModal = closeNestedNestedModal;
window.toggleSensorsAccountFilterExpanded = toggleSensorsAccountFilterExpanded;
window.toggleSensorsAccountTreeItem = toggleSensorsAccountTreeItem;
window.applySensorsAccountFilter = applySensorsAccountFilter;
window.clearSensorsAccountFilter = clearSensorsAccountFilter;
window.applySensorsBatteryFilter = applySensorsBatteryFilter;
window.clearSensorsFilters = clearSensorsFilters;
window.loadSensorsData = loadSensorsData;
window.sortSensorsTable = sortSensorsTable;
window.showSensorColumnMenu = showSensorColumnMenu;
window.toggleSensorColumn = toggleSensorColumn;
window.onSensorSelectChange = onSensorSelectChange;
window.toggleSelectAllSensors = toggleSelectAllSensors;
window.clearSensorSelection = clearSensorSelection;
window.showBulkAssignAccountModal = showBulkAssignAccountModal;
window.confirmBulkAssignAccount = confirmBulkAssignAccount;
window.resetSensorColumnsToDefault = resetSensorColumnsToDefault;

// Load column preferences on module initialization
loadSensorsColumnPreferences();
